import { isValidElement } from "react";
import { formatReportCellValue } from "@/pages/reports/reportTableHelper";
import { formatReportColumnLabel } from "@/utility/fclReportHelpers";
import { cell, pickField } from "@/utility/fclReportFieldUtils";
import {
    getContainerTeusFromRow,
    getContainerTypeFromRow,
} from "@/utility/fclReportHelpers";

/** Keys never exported (internal ids — not shown in report tables). */
export const REPORT_EXPORT_EXCLUDE_KEYS = [
    "id",
    "bolId",
    "BolId",
    "BOLId",
    "bolID",
    "companyId",
    "CompanyId",
    "compnayId",
    "bookingId",
    "BookingId",
    "customerId",
    "CustomerId",
    "carrierId",
    "CarrierId",
];

const EXCLUDE_LOWER = new Set(REPORT_EXPORT_EXCLUDE_KEYS.map((k) => k.toLowerCase()));

export const isExcludedExportKey = (key) => {
    if (!key) return true;
    const lower = String(key).toLowerCase();
    if (EXCLUDE_LOWER.has(lower)) return true;
    if (lower === "id") return true;
    if (lower.endsWith("id") && (lower.includes("bol") || lower.includes("company"))) return true;
    return false;
};

export const toExcelCellValue = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : "";
    }
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (value instanceof Date) return value.toISOString();
    if (isValidElement(value)) return "";
    if (typeof value === "object") {
        try {
            return JSON.stringify(value);
        } catch {
            return String(value);
        }
    }
    return String(value).replace(/[\x00-\x08\x0B\x0C\x0E-\x1F]/g, "");
};

const COLUMN_EXPORT_FALLBACKS = {
    teus: (row) => Number(pickField(row, ["teus", "Teus", "totalTeus", "TotalTeus"], 0)).toFixed(2),
    fileRefNumber: (row) =>
        cell(pickField(row, ["fileRefNumber", "FileRefNumber", "fileRefNo", "FileRefNo"])),
    fileRefNo: (row) =>
        cell(pickField(row, ["fileRefNo", "FileRefNo", "referenceNo", "ReferenceNo"])),
    placeOfDelivery: (row) =>
        cell(
            pickField(row, [
                "shipezPlaceOfDelivery",
                "ShipezPlaceOfDelivery",
                "carrierPlaceOfDelivery",
                "CarrierPlaceOfDelivery",
            ])
        ),
    events: (row) => {
        const events =
            row?.containerTrackingSummary ??
            row?.ContainerTrackingSummary ??
            row?.trackingEvents ??
            [];
        return Array.isArray(events) ? `${events.length} event(s)` : "-";
    },
};

const resolveFromRender = (row, col) => {
    try {
        if (typeof col.exportValue === "function") {
            return toExcelCellValue(col.exportValue(row));
        }
        if (COLUMN_EXPORT_FALLBACKS[col.key]) {
            return toExcelCellValue(COLUMN_EXPORT_FALLBACKS[col.key](row));
        }
        if (typeof col.getTooltip === "function") {
            const tip = col.getTooltip(row);
            if (tip !== undefined && tip !== null && String(tip).trim() !== "") {
                return toExcelCellValue(tip);
            }
        }
        if (col.fieldKeys?.length) {
            if (typeof col.render === "function") {
                const rendered = col.render(row);
                if (typeof rendered === "string" || typeof rendered === "number") {
                    return toExcelCellValue(rendered);
                }
                if (!isValidElement(rendered)) {
                    return toExcelCellValue(rendered);
                }
            }
            return toExcelCellValue(formatReportCellValue(pickField(row, col.fieldKeys)));
        }
        if (col.key === "containerType") {
            return toExcelCellValue(getContainerTypeFromRow(row));
        }
        if (col.key === "totalTues" || col.key === "totalTeus") {
            return toExcelCellValue(getContainerTeusFromRow(row));
        }
        if (typeof col.render === "function") {
            const rendered = col.render(row);
            if (typeof rendered === "string" || typeof rendered === "number") {
                return toExcelCellValue(rendered);
            }
            if (!isValidElement(rendered)) {
                return toExcelCellValue(rendered);
            }
        }
        return toExcelCellValue(formatReportCellValue(row?.[col.key]));
    } catch {
        return "";
    }
};

/** Map API rows to flat objects matching table column keys only. */
export const mapRowsForExcelExport = (rows = [], columns = []) => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    if (!columns?.length) {
        return filterRowsForExcelExport(rows);
    }
    return rows.map((row) => {
        const out = {};
        columns.forEach((col) => {
            if (!col?.key || isExcludedExportKey(col.key)) return;
            out[col.key] = resolveFromRender(row, col);
        });
        return out;
    });
};

/** Fallback when no column defs: drop excluded keys from each row. */
export const filterRowsForExcelExport = (rows = []) => {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    return rows.map((row) => {
        const out = {};
        Object.keys(row || {}).forEach((key) => {
            if (!isExcludedExportKey(key)) {
                out[key] = toExcelCellValue(formatReportCellValue(row[key]));
            }
        });
        return out;
    });
};

export const getExcelColumnHeader = (col) => {
    if (typeof col?.label === "string" && col.label.trim()) return col.label;
    return formatReportColumnLabel(col?.key || "");
};

export const buildExcelColumnDefs = (columns = []) =>
    columns
        .filter((col) => col?.key && !isExcludedExportKey(col.key))
        .map((col) => ({
            key: col.key,
            header: getExcelColumnHeader(col),
        }));
